/* global graphql */
import React, { Component } from 'react';
import { Container, Row, Col } from 'react-bs-grid';
import BodyClassName from 'react-body-classname';
import Helmet from 'react-helmet';
import { ChevronUp } from 'react-feather';
import {
  Svg,
  Circle,
  Arc,
  Group,
  Rect,
  // Area,
  Ribbon,
} from '@potion/element';
import {
  Pack,
  Grid,
  Pie,
  Partition,
  // Treemap,
  Stack,
  Chord,
  Cluster,
} from '@potion/layout';
import Link from 'gatsby-link';
import WindowSize from 'react-window-size';
import get from 'lodash/get';
import { Fab, FabButton } from 'react-fab';

import Space from '../components/Space';
import Title from '../components/Title';
import Page from '../components/Page';
import { COLORS } from '../constants';

class Element extends Component {
  componentDidMount() {
    if (Prism) Prism.highlightAll();
  }

  getSideLength() {
    const { windowWidth } = this.props;
    let sideLength = 340 * 2;
    if (windowWidth < 1200) sideLength = 270 * 2;
    if (windowWidth < 992) sideLength = 210 * 2;
    if (windowWidth < 768) sideLength = 258 * 2;
    if (windowWidth < 570) sideLength = windowWidth - 100;
    if (windowWidth === 0) sideLength = 340;
    return sideLength;
  }

  getDocs() {
    const edges = get(this.props, 'data.allMarkdownRemark.edges');
    if (!edges) return '';
    const { html } = edges.filter(edge => edge.node.html.indexOf('name="layouts"') > -1)[0].node;
    return html.slice(html.indexOf('<h2>'));
  }

  scrollTo(key) {
    return () => {
      document.querySelector(`[name="layouts__${key}"]`).scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    };
  }

  render() {
    const sideLength = this.getSideLength();
    const height = (sideLength / 3) * 2;
    return (
      <BodyClassName className={'no-padding'}>
        <Page fluid name="page-build">
          <Helmet
            title="Potion.js - React + D3"
            meta={[
              {
                property: 'og:url',
                content: 'http://numberpicture.com/build',
              },
              {
                property: 'og:title',
                content: 'Potion.js - React + D3',
              },
              {
                property: 'og:description',
                content: 'Low-level React components for declaratively composing animated, interactive SVG visualizations.',
              },
              {
                name: 'twitter:title',
                content: 'Potion.js - React + D3',
              },
              {
                name: 'twitter:description',
                content: 'Low-level React components for declaratively composing animated, interactive SVG visualizations.',
              },
              {
                charSet: 'utf-8',
              },
            ]}
          />

          <Fab style={{ right: '20px', bottom: '20px' }}>
            <FabButton>
              <a onClick={this.scrollTo('top')}>
                <ChevronUp />
              </a>
            </FabButton>
          </Fab>

          <Container fluid>
            <Row>
              <Container>
                <Row>
                  <Col lg={12}>
                    <div name="layouts__top" />
                    <Title>Layouts</Title>
                  </Col>
                </Row>
                <Row>
                  <Col lg={8}>
                    <p>Potion Layouts are React components that define the structure of visualizations. They transform and enrich data with added properties that are useful for layouting shapes out on the page when creating visualizations.</p>
                    <p>Use these in combination with <Link to="/element">Potion Elements</Link> and other utilities to construct custom animated visualizations from the ground up.</p>
                  </Col>
                  <Space half />
                </Row>

                <Row>

                  <Col md={12}>
                    <a onClick={this.scrollTo('chord')}>
                      <h3>Chord</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Chord
                            data={[
                              [11975, 5871, 8916, 2868],
                              [1951, 10048, 2060, 6171],
                              [8010, 16145, 8090, 8045],
                              [1013, 990, 940, 6907],
                            ]}
                            springDamping={20}
                            animate
                            nodeEnter={d => ({
                              ...d,
                              sourceStartAngle: d.sourceEndAngle,
                              targetStartAngle: d.targetEndAngle,
                            })}
                          >{nodes => nodes.map(node =>
                              (<Ribbon
                                {...node}
                                fill={COLORS.DARK_GREY}
                                stroke={COLORS.DARK_GREY}
                                fillOpacity={0.9}
                                radius={height * 0.4}
                                transform={{ translate: [sideLength / 2, height / 2] }}
                              />))}
                          </Chord>
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={12}>
                    <a onClick={this.scrollTo('cluster')}>
                      <h3>Cluster</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Group transform={{ translate: [sideLength * 0.1, height * 0.2] }}>
                            <Cluster
                              data={{
                                children: [
                                  {
                                    value: 10,
                                    key: '1',
                                    children: [
                                      { value: 1, key: '1a1' },
                                      { value: 2, key: '1a2' },
                                      {
                                        value: 3,
                                        key: '1a3',
                                        children: [
                                          { value: 0.1, key: '1b1' },
                                          { value: 0.2, key: '1b2' },
                                        ],
                                      },
                                    ],
                                  },
                                  {
                                    value: 20,
                                    key: '2',
                                    children: [
                                      { value: 1, key: '2a1' },
                                      { value: 2, key: '2a2' },
                                      {
                                        value: 3,
                                        key: '2a3',
                                        children: [
                                          { value: 0.1, key: '2b1' },
                                          { value: 0.2, key: '2b2' },
                                        ],
                                      },
                                    ],
                                  },
                                ],
                              }}
                              size={[height * 0.6, sideLength * 0.8]}
                              nodeEnter={d => ({ ...d, x: sideLength / 2, y: height / 2 })}
                              animate
                            >{nodes => nodes.map(({ key, x, y }) =>
                                (<Circle
                                  key={key}
                                  cx={y}
                                  cy={x}
                                  r={10}
                                  fill={COLORS.DARK_GREY}
                                />))}</Cluster>
                          </Group>
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={12}>
                    <a onClick={this.scrollTo('grid')}>
                      <h3>Grid</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Grid
                            data={[
                              { value: 1, key: '1' },
                              { value: 2, key: '2' },
                              { value: 3, key: '3' },
                              { value: 4, key: '4' },
                              { value: 5, key: '5' },
                              { value: 6, key: '6' },
                              { value: 7, key: '7' },
                              { value: 8, key: '8' },
                              { value: 9, key: '9' },
                              { value: 10, key: '10' },
                            ]}
                            bands
                            size={[sideLength, height]}
                            nodeEnter={d => ({ ...d, x: sideLength / 2, y: height / 2 })}
                            animate
                          >{nodes => nodes.map(({ nodeWidth, nodeHeight, x, y, key, value }) =>
                              (<Circle
                                key={key}
                                cx={x + (nodeWidth / 2)}
                                cy={y + (nodeHeight / 2)}
                                r={value}
                                fill={COLORS.DARK_GREY}
                              />))
                            }</Grid>
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={12}>
                    <a onClick={this.scrollTo('pack')}>
                      <h3>Pack</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Group transform={{ translate: [0, height * 0.2] }}>
                            <Pack
                              data={{
                                children: [
                                  { value: 1, key: '1' },
                                  { value: 2, key: '2' },
                                  { value: 3, key: '3' },
                                ],
                              }}
                              sum={datum => datum.value}
                              size={[sideLength, height * 0.6]}
                              includeRoot={false}
                              nodeEnter={d => ({ ...d, r: 0 })}
                              animate
                            >{nodes => nodes.map(({ x, y, r, key }) =>
                                (<Circle
                                  key={key}
                                  cx={x}
                                  cy={y}
                                  r={r}
                                  fill={COLORS.DARK_GREY}
                                />
                                ))
                              }</Pack>
                          </Group>
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={12}>
                    <a onClick={this.scrollTo('partition')}>
                      <h3>Partition</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Partition
                            data={{
                              children: [
                                { value: 1, key: '1' },
                                { value: 2, key: '2' },
                                {
                                  value: 0,
                                  key: '3',
                                  children: [
                                    { value: 1, key: 'a1' },
                                    { value: 2, key: 'a2' },
                                    {
                                      value: 0,
                                      key: 'a3',
                                      children: [
                                        { value: 1, key: 'b1' },
                                        { value: 2, key: 'b2' },
                                        {
                                          value: 3,
                                          key: 'b3',
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            }}
                            sum={datum => datum.value}
                            size={[sideLength, height * 0.6]}
                            nodeEnter={d => ({ ...d, r: 0 })}
                            animate
                          >{nodes => nodes.map(({ key, x0, y0, x1, y1 }) =>
                              (<Rect
                                key={key}
                                x={x0}
                                y={y0}
                                width={x1 - x0}
                                height={y1 - y0}
                                fill={COLORS.DARK_GREY}
                                stroke="white"
                                transform={{ translate: [0, height * 0.2] }}
                              />
                              ))}
                          </Partition>
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={12}>
                    <a onClick={this.scrollTo('pie')}>
                      <h3>Pie</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Group transform={{ translate: [sideLength / 2, height / 2] }}>
                            <Pie
                              data={[
                                { id: 1, key: '1', value: 1 },
                                { id: 2, key: '2', value: 2 },
                                { id: 3, key: '3', value: 3 },
                              ]}
                              value={datum => datum.value}
                              id={datum => datum.id}
                              sort={(a, b) => a.id - b.id}
                              nodeEnter={d => ({ ...d, startAngle: d.endAngle })}
                              animate
                            >{nodes => nodes.map(({ startAngle, endAngle, key }) =>
                                (<Arc
                                  key={key}
                                  innerRadius={0}
                                  outerRadius={height / 4}
                                  startAngle={startAngle}
                                  endAngle={endAngle}
                                  fill={COLORS.DARK_GREY}
                                  stroke="white"
                                  strokeWidth={1}
                                />))
                              }</Pie>
                          </Group>
                        </Svg>
                      </div>
                    </a>
                  </Col>

                  <Col md={12}>
                    <a onClick={this.scrollTo('stack')}>
                      <h3>Stack</h3>
                      <div className="chart">
                        <Svg width={sideLength} height={height}>
                          <Stack
                            data={[
                              { a: 0.1 * height, b: 0.1 * height, c: 0.2 * height, key: '1' },
                              { a: 0.2 * height, b: 0.1 * height, c: 0.2 * height, key: '2' },
                              { a: 0.3 * height, b: 0.1 * height, c: 0.2 * height, key: '3' },
                            ]}
                            keys={['a', 'b', 'c']}
                            animate
                          >{nodes => nodes.map((node, i) =>
                              (<Group transform={{ rotate: [180, sideLength / 2, height / 2] }} key={node.key}>
                                <Rect
                                  x={sideLength * 0.1}
                                  y={(height * 0.2) + node[0][0]}
                                  width={sideLength * 0.2}
                                  height={node[0][1] - node[0][0]}
                                  fill={COLORS.DARK_GREY}
                                  stroke="white"
                                />
                                <Rect
                                  x={sideLength * 0.4}
                                  y={(height * 0.2) + node[1][0]}
                                  width={sideLength * 0.2}
                                  height={node[1][1] - node[1][0]}
                                  fill={COLORS.DARK_GREY}
                                  stroke="white"
                                />
                                <Rect
                                  x={sideLength * 0.7}
                                  y={(height * 0.2) + node[2][0]}
                                  width={sideLength * 0.2}
                                  height={node[2][1] - node[2][0]}
                                  fill={COLORS.DARK_GREY}
                                  stroke="white"
                                />
                              </Group>))}</Stack>
                        </Svg>
                      </div>
                    </a>
                  </Col>

                </Row>

                <div className="page page-docs">
                  <div className="content">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: this.getDocs(),
                      }}
                    />
                  </div>
                </div>

                <Space />
                <Space />

              </Container>
            </Row>
          </Container>
        </Page>
      </BodyClassName>
    );
  }
}

export default WindowSize(Element);

export const pageQuery = graphql`
  query LayoutQuery {
    allMarkdownRemark {
      edges {
        node {
          html
          headings {
            depth
            value
          }
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/pages/layout.js